import { FormEvent, useMemo, useState } from 'react';
import { Col, Form, InputGroup, Row } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import Button from 'components/base/Button';
import AuthSimpleLayout from 'layouts/AuthSimpleLayout';
import { register } from 'features/auth/authApi';
import { UserRole } from 'features/auth/authSession';
import {
  Coordinates,
  geocodeAddress,
  reverseGeocode
} from 'features/location/googleLocation';
import LocationAutocomplete from 'components/common/LocationAutocomplete';

const PHARMACY_DOCUMENT_FIELDS = [
  {
    key: 'passport',
    label: 'Passport',
    required: true
  },
  {
    key: 'visa_work_permit',
    label: 'Visa/Work permit',
    required: false
  },
  {
    key: 'enhanced_dbs_check',
    label:
      'Enhanced DBS Check (we can help you arrange this if required - please contact us)',
    required: false
  },
  {
    key: 'national_insurance',
    label: 'National Insurance',
    required: true
  },
  {
    key: 'gphc_number_doc',
    label: 'GPhC Number',
    required: true
  },
  {
    key: 'smartcard',
    label: 'Smartcard',
    required: true
  },
  {
    key: 'safeguarding_level_2',
    label: 'Safeguarding level 2',
    required: true
  },
  {
    key: 'professional_reference_1',
    label: 'Professional reference 1',
    required: true
  },
  {
    key: 'professional_reference_2',
    label: 'Professional reference 2',
    required: true
  }
] as const;

const LOCUM_DOCUMENT_FIELDS = [
  {
    key: 'passport',
    label: 'Passport',
    required: true
  },
  {
    key: 'visa_work_permit',
    label: 'Visa/Work permit',
    required: false
  },
  {
    key: 'national_insurance',
    label: 'National Insurance',
    required: true
  },
  {
    key: 'qualification_certificates',
    label: 'Qualification Certificates',
    required: true
  },
  {
    key: 'professional_reference_1',
    label: 'Professional reference 1',
    required: true
  },
  {
    key: 'professional_reference_2',
    label: 'Professional reference 2',
    required: true
  }
] as const;

type PharmacyDocumentKey = (typeof PHARMACY_DOCUMENT_FIELDS)[number]['key'];
type LocumDocumentKey = (typeof LOCUM_DOCUMENT_FIELDS)[number]['key'];
type DocumentKey = PharmacyDocumentKey | LocumDocumentKey;

const renderDocumentLabel = (field: {
  key: DocumentKey;
  label: string;
  required: boolean;
}) => {
  if (field.key === 'enhanced_dbs_check') {
    return (
      <>
        <span>Enhanced DBS Check</span>
        <span className="d-block text-body-tertiary fs-10">
          (we can help you arrange this if required - please contact us)
        </span>
      </>
    );
  }

  if (field.key === 'visa_work_permit') {
    return (
      <>
        <span>Visa/Work permit</span>
        <span className="d-block text-body-tertiary fs-10">(if required)</span>
      </>
    );
  }

  return <span>{field.label}</span>;
};

const RegisterPage = () => {
  const navigate = useNavigate();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [location, setLocation] = useState('');
  const [role, setRole] = useState<UserRole>('locum');

  const [phone, setPhone] = useState('');
  const [qualifications, setQualifications] = useState('');
  const [experienceYears, setExperienceYears] = useState('');
  const [travelDistance, setTravelDistance] = useState('');
  const [locumRole, setLocumRole] = useState<'pharmacist' | 'technician'>(
    'pharmacist'
  );

  const [pharmacyName, setPharmacyName] = useState('');
  const [contactPerson, setContactPerson] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');

  const [documentFiles, setDocumentFiles] = useState<
    Partial<Record<DocumentKey, File | null>>
  >({});

  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [detectedCoordinates, setDetectedCoordinates] =
    useState<Coordinates | null>(null);

  const isLocum = useMemo(() => role === 'locum', [role]);

  const handleDocumentFileChange = (docKey: DocumentKey, file: File | null) => {
    setDocumentFiles(prev => ({
      ...prev,
      [docKey]: file
    }));
  };

  const getCurrentPosition = () =>
    new Promise<GeolocationPosition>((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new Error('Geolocation is not supported by this browser'));
        return;
      }

      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000
      });
    });

  const handleUseCurrentLocation = async () => {
    setErrorMessage('');
    setIsDetectingLocation(true);

    try {
      const position = await getCurrentPosition();
      const coords = {
        lat: position.coords.latitude,
        lng: position.coords.longitude
      };

      setDetectedCoordinates(coords);

      try {
        const formattedAddress = await reverseGeocode(coords);
        if (formattedAddress) {
          setLocation(formattedAddress);
        }
      } catch {
        // Ignore reverse geocode failure (e.g. missing Google API key) during testing.
      }
    } catch (error) {
      setErrorMessage(
        error instanceof Error
          ? error.message
          : 'Unable to detect your current location'
      );
    } finally {
      setIsDetectingLocation(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMessage('');
    setIsLoading(true);

    try {
      // Validate location is selected (only for locums)
      if (isLocum && !location.trim()) {
        setErrorMessage('Please select a location');
        setIsLoading(false);
        return;
      }

      // Validate coordinates are captured (only for locums)
      if (
        isLocum &&
        (!detectedCoordinates ||
          !detectedCoordinates.lat ||
          !detectedCoordinates.lng)
      ) {
        setErrorMessage('Please select a valid location from the suggestions');
        setIsLoading(false);
        return;
      }

      const resolvedLocation = isLocum ? location.trim() : '';
      const resolvedCoordinates = detectedCoordinates;

      const payload = new FormData();

      payload.append('name', name);
      payload.append('email', email);
      payload.append('password', password);
      payload.append('role', role);

      if (isLocum) {
        payload.append('location', resolvedLocation);
        payload.append('latitude', String(resolvedCoordinates!.lat));
        payload.append('longitude', String(resolvedCoordinates!.lng));
        payload.append('phone', phone);
        payload.append('qualifications', qualifications);
        payload.append('experience_years', String(Number(experienceYears)));
        payload.append('travel_distance', String(Number(travelDistance)));
        payload.append('locum_role', locumRole);

        // Add locum documents
        for (const field of LOCUM_DOCUMENT_FIELDS) {
          const file = documentFiles[field.key];
          if (file) {
            payload.append(field.key, file);
          }
        }
      } else {
        // Pharmacy role - location can be added later in profile page
        payload.append('pharmacy_name', pharmacyName);
        payload.append('contact_person', contactPerson);
        payload.append('license_number', licenseNumber);

        // Add pharmacy documents
        for (const field of PHARMACY_DOCUMENT_FIELDS) {
          const file = documentFiles[field.key];
          if (file) {
            payload.append(field.key, file);
          }
        }
      }

      await register(payload);
      navigate('/auth/login', { replace: true });
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'Register failed'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <AuthSimpleLayout className="col-xl-10 col-xxl-9">
      <div className="mx-auto" style={{ maxWidth: '1240px' }}>
        <div className="text-center mb-6">
          <h3 className="text-body-highlight">Register</h3>
        </div>

        <Form onSubmit={handleSubmit}>
          <Row className="g-3">
            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Enter full name"
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Email <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="name@example.com"
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Password <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="Strong password"
                  required
                />
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="text-start">
                <Form.Label>
                  Role <span className="text-danger">*</span>
                </Form.Label>
                <Form.Select
                  value={role}
                  onChange={e => setRole(e.target.value as UserRole)}
                >
                  <option value="locum">Locum</option>
                  <option value="pharmacy">Pharmacy</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={12}>
              {isLocum && (
                <LocationAutocomplete
                  value={location}
                  onChange={setLocation}
                  onCoordinatesChange={setDetectedCoordinates}
                  label="Location"
                  placeholder="Search by address / area"
                  required
                />
              )}
            </Col>
          </Row>

          {isLocum ? (
            <>
              <Row className="g-3 mt-1">
                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Phone <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      value={phone}
                      onChange={e => setPhone(e.target.value)}
                      placeholder="Enter contact number"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>Qualifications</Form.Label>
                    <Form.Control
                      value={qualifications}
                      onChange={e => setQualifications(e.target.value)}
                      placeholder="PharmD, B.Pharm, etc."
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Experience Years <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={experienceYears}
                      onChange={e => setExperienceYears(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Travel Distance (km){' '}
                      <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="number"
                      min={0}
                      value={travelDistance}
                      onChange={e => setTravelDistance(e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Your Role <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Select
                      value={locumRole}
                      onChange={e =>
                        setLocumRole(
                          e.target.value as 'pharmacist' | 'technician'
                        )
                      }
                      required
                    >
                      <option value="pharmacist">Pharmacist</option>
                      <option value="technician">Technician</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <div className="border rounded-3 p-3 mt-4 bg-body-emphasis">
                <h5 className="mb-2 text-body-highlight">Locum documents</h5>
                <p className="text-body-tertiary fs-9 mb-3">
                  Accepted format: PDF, Word, image.
                </p>

                <Row className="g-3">
                  {LOCUM_DOCUMENT_FIELDS.map(field => (
                    <Col xs={12} md={6} key={field.key}>
                      <Form.Group className="text-start border rounded-2 p-3 h-100 bg-body">
                        <Form.Label className="mb-1 d-block">
                          {renderDocumentLabel(field)}
                          {field.required ? (
                            <span className="text-danger"> *</span>
                          ) : (
                            <span className="text-body-tertiary"></span>
                          )}
                        </Form.Label>
                        <Form.Control
                          type="file"
                          accept=".pdf,.doc,.docx,image/*"
                          onChange={e =>
                            handleDocumentFileChange(
                              field.key,
                              (e.target as HTMLInputElement).files?.[0] ?? null
                            )
                          }
                          required={field.required}
                        />
                        {documentFiles[field.key] && (
                          <Form.Text className="text-body-tertiary">
                            Selected: {documentFiles[field.key]?.name}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </div>
            </>
          ) : (
            <>
              <Row className="g-3 mt-1">
                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Pharmacy Name <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      value={pharmacyName}
                      onChange={e => setPharmacyName(e.target.value)}
                      placeholder="Enter pharmacy name"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="text-start">
                    <Form.Label>
                      Contact Person <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      value={contactPerson}
                      onChange={e => setContactPerson(e.target.value)}
                      placeholder="Owner / Manager name"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col xs={12}>
                  <Form.Group className="text-start">
                    <Form.Label>License Number</Form.Label>
                    <Form.Control
                      value={licenseNumber}
                      onChange={e => setLicenseNumber(e.target.value)}
                      placeholder="Optional"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <div className="border rounded-3 p-3 mt-4 bg-body-emphasis">
                <h5 className="mb-2 text-body-highlight">Pharmacy documents</h5>
                <p className="text-body-tertiary fs-9 mb-3">
                  Accepted format: PDF, Word, image.
                </p>

                <Row className="g-3">
                  {PHARMACY_DOCUMENT_FIELDS.map(field => (
                    <Col
                      xs={12}
                      md={field.key === 'enhanced_dbs_check' ? 12 : 6}
                      key={field.key}
                    >
                      <Form.Group className="text-start border rounded-2 p-3 h-100 bg-body">
                        <Form.Label className="mb-1 d-block">
                          {renderDocumentLabel(field)}
                          {field.required ? (
                            <span className="text-danger"> *</span>
                          ) : (
                            <span className="text-body-tertiary"></span>
                          )}
                        </Form.Label>
                        <Form.Control
                          type="file"
                          accept=".pdf,.doc,.docx,image/*"
                          onChange={e =>
                            handleDocumentFileChange(
                              field.key,
                              (e.target as HTMLInputElement).files?.[0] ?? null
                            )
                          }
                          required={field.required}
                        />
                        {documentFiles[field.key] && (
                          <Form.Text className="text-body-tertiary">
                            Selected: {documentFiles[field.key]?.name}
                          </Form.Text>
                        )}
                      </Form.Group>
                    </Col>
                  ))}
                </Row>
              </div>
            </>
          )}

          {errorMessage && (
            <div className="alert alert-danger py-2 mt-3" role="alert">
              {errorMessage}
            </div>
          )}

          <Button
            type="submit"
            variant="primary"
            className="w-100 mb-3 mt-4"
            disabled={isLoading || isDetectingLocation}
          >
            {isLoading ? 'Registering...' : 'Register'}
          </Button>

          <div className="text-center fs-9">
            Already have an account? <Link to="/auth/login">Login</Link>
          </div>
        </Form>
      </div>
    </AuthSimpleLayout>
  );
};

export default RegisterPage;
